import {
    REPLACE_CART,
    RESET_CART,
    SET_BLOCKED_CART,
    SET_CART_PRODUCT_ERROR,
    SET_CART_TEMP_ITEM,
    SET_IS_FETCHING_CART,
    SET_ORDER_ADDRESS,
} from 'reducers/actions-types';
import logError from 'util/misc/log-error';
import { curry, get } from 'lodash';
import { dispatchError } from 'reducers/helpers';
import { MAX_ITEMS } from 'constants/cart';
import tracking from 'tracking';
import { cart as cartURL, cartItems as cartItemsURL } from 'constants/api-urls';

import { productFactoryMiddleware } from 'util/misc/products-factory-middleware';

import axios from 'axios';
import promisesSequencer from 'util/misc/promises-sequencer';
import { OPEN } from 'constants/order-states';
import checkDuplicatedPromotions from '../../../util/debug/promotion-code-logger';

/* eslint-disable camelcase */

// array that will contain all the product ids queued waiting for an updata
const productsUpdatingIDs = [];


/**
 * Callback triggered when all the products updates are done
 * @returns {Array} - cart items
 */
function onUpdateDone({ getState, dispatch }, res) {
    const state = getState();
    productsUpdatingIDs.shift();

    if (!productsUpdatingIDs.length && state.cart.isFetching) {
        dispatch({ type: SET_IS_FETCHING_CART, fetchingId: null, isFetching: false });
    }

    return res;
}

/**
 * Callback called when a product update event goes wrong
 * @param {function} dispatch
 * @param {string} fetchingId
 * @param {function} getState
 * @param {Error} e
 * @returns {Promise} - refetch the cart
 */
function onUpdateError({ dispatch, fetchingId, getState }, e) {
    if (!productsUpdatingIDs.length) {
        return Promise.resolve();
    }

    // clear the queue
    dispatch({ type: SET_CART_PRODUCT_ERROR, error: { fetchingId, exception: e } });
    dispatch({ type: SET_IS_FETCHING_CART, fetchingId: null, isFetching: false });
    productsUpdatingIDs.splice(0, productsUpdatingIDs.length);

    if (e.status && ![400, 404].includes(e.status)) {
        dispatchError(dispatch, e, {
            url: cartItemsURL(),
            reloadButton: true,
            description: e.statusText,
        });
    } else {
        dispatchError(dispatch, e, {
            url: cartItemsURL(),
            reloadButton: true,
            description: e.message,
        });
    }

    return fetch({ dispatch }, getState);
}

/**
 * Update the current cart on the server
 * @param {IUpdateData} data - The update to be performed
 * @param {function} dispatch - The action dispatcher
 * @param {Product} product
 * @param {object} currentState
 * @param {function} getState
 * @returns {number} - The new orderId (if it was not yet defined before)
 */
export function update({ data, dispatch, product, currentState }, getState) {
    const fetchingId = data ? data.product_id : 0;
    const onDone = curry(onUpdateDone)({ getState, dispatch });
    const onError = curry(onUpdateError)({ fetchingId, dispatch, getState });
    const cooperative = get(getState(), 'region.active.cooperative');

    dispatch({ type: SET_CART_TEMP_ITEM, tempItem: null }); // Clear the temp item
    productsUpdatingIDs.push(fetchingId);

    promisesSequencer(() => {
        dispatch({ type: SET_IS_FETCHING_CART, fetchingId, isFetching: true });

        return axios.post(cartItemsURL(), data)
            .then(({ data: result, status }) => {
                if (status > 399) {
                    logError(new Error(`Unable to update the cart "status=${ status }"`));
                    return [];
                }

                let { items = [] } = result;

                items = items.filter(item => item.id !== fetchingId && !productsUpdatingIDs.includes(item.id)); // eslint-disable-line

                const {
                    total_items_price = 0,
                    shipping_price = 0,
                    estimated_customer_price = 0,
                    customer_delivery_fee = 0,
                    bag_information = {},
                    promotional_code = null,
                    promotions,
                    address_id = null,
                    id,
                    state,
                } = result;

                const oldProduct = currentState.cart.items[product.id];
                const trackingData = Object.assign({}, data, {
                    amount: Math.abs(oldProduct ? oldProduct.amount - data.amount : data.amount),
                });

                if (oldProduct && oldProduct.amount > data.amount) {
                    tracking.removeFromCart(product, trackingData);
                } else {
                    tracking.addToCart(product, trackingData);
                }


                // Order is important here for cart counter to work correctly
                // If order is reversed, and you click the plus or minus button,
                // there will be a flicker of the old number in the amount input
                // as the products-list will pass the old product back in
                dispatch({
                    type: REPLACE_CART,
                    items: items.map(item => { //eslint-disable-line
                        item.product = productFactoryMiddleware(item.product, { cooperative });
                        return item;
                    }),
                    id,
                    state,
                    customerDeliveryFee: customer_delivery_fee,
                    estimatedCustomerPrice: estimated_customer_price,
                    shippingPrice: shipping_price,
                    totalItemsPrice: total_items_price,
                    bagInfo: bag_information,
                    promotionalCode: promotional_code,
                    promotions,
                });

                dispatch({ type: SET_ORDER_ADDRESS, addressId: address_id });
                return result;
            });
    }).then(onDone, onError);
}

/**
 * Set a new cart
 * @param {function} dispatch
 * @param {function} getState
 * @param {ICart} cart
 */
export function setCart(dispatch, getState, cart) {
    const cooperative = get(getState(), 'region.active.cooperative');
    const {
        items = [],
        total_items_price = 0,
        shipping_price = 0,
        estimated_customer_price = 0,
        customer_delivery_fee = 0,
        bag_information = {},
        promotions,
        promotional_code = null,
        id,
        state,
    } = cart;

    if (state === OPEN) {
        dispatch({
            type: REPLACE_CART,
            items: items.map(item => { //eslint-disable-line
                item.product = productFactoryMiddleware(item.product, { cooperative });
                return item;
            }),
            id,
            state,
            customerDeliveryFee: customer_delivery_fee,
            estimatedCustomerPrice: estimated_customer_price,
            shippingPrice: shipping_price,
            totalItemsPrice: total_items_price,
            bagInfo: bag_information,
            promotionalCode: promotional_code,
            promotions,
        });
    } else {
        dispatch(block({ state, id }));
    }
}

/**
 * Fetch the current cart from the server
 * @param {function} dispatch - The action dispatcher
 * @param {function} getState - Get state function
 * @returns {number} - The new orderId (if it was not yet defined before)
 */
export function fetch({ dispatch }, getState) {
    dispatch({ type: SET_IS_FETCHING_CART, isFetching: true });

    return axios.get(cartURL())
        .then(({ data: result, status }) => {
            if (status > 399) {
                logError(new Error(`Unable to fetch the cart items "status=${ status }"`));
            }

            checkDuplicatedPromotions(result, true, cartURL());
            setCart(dispatch, getState, result);

            const {
                address_id = null,
            } = result;

            dispatch({ type: SET_ORDER_ADDRESS, addressId: address_id });
            dispatch({ type: SET_IS_FETCHING_CART, fetchingId: null, isFetching: false });
        })
        .catch((e) => {
            dispatch({ type: SET_IS_FETCHING_CART, isFetching: false });
            // we decided that the empty cart will return the 400 or 404 codes back
            // so technically it shouldn't be an error that we want to display
            if (e.status && [400, 404].includes(e.status)) {
                // cart is blocked
                dispatch({ type: RESET_CART, wasFetched: true });
            } else {
                // hard error
                dispatch({ type: RESET_CART });
                dispatchError(dispatch, e, {
                    url: cartItemsURL(),
                    description: e.message,
                });
            }
        });
}

/**
 * Clear the current cart on the server
 * @param data - The update to be performed
 * @param {function} dispatch - The action dispatcher
 * @returns {number} - The new orderId (if it was not yet defined before)
 */
export function clear({ data, dispatch }) {
    dispatch({ type: SET_IS_FETCHING_CART, isFetching: true });

    return axios.delete(cartItemsURL(), data)
        .then(() => {
            dispatch({ type: SET_IS_FETCHING_CART, isFetching: false });
            dispatch({ type: RESET_CART, wasFetched: true });
        })
        .catch((e) => {
            dispatchError(dispatch, e, {
                url: cartURL(),
                description: e.message,
            });

            dispatch({ type: SET_IS_FETCHING_CART, isFetching: false });
            dispatch({ type: RESET_CART });
        });
}

/**
 * Block the current cart
 * @param {string} state
 * @param {string} id
 * @return {function}
 */
export function block({ state, id }) {
    return (dispatch) => {
        dispatch({
            type: SET_BLOCKED_CART,
            id: id,
            state,
        });
    };
}

/**
 * Limit a number to a maximum of items
 * @param {number} number
 * @return {number}
 */
export function limitAmount(number) {
    return Math.max(0, Math.min(MAX_ITEMS, number));
}



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/cart/helpers.js